import {
  useWSModal,
  WSForm,
  WSInputDate,
  WSInputNumber,
  WSInputText,
  WSInputTextarea,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { DeductionType } from "@wingspanhq/payments/dist/interfaces/deductions";
import { Currency } from "@wingspanhq/users/dist/lib/interfaces";
import React from "react";
import * as Yup from "yup";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { SelectPayee } from "../../../../modules/Payees/components/SelectPayee";
import { useUserId } from "../../../../query/hooks/helpers";
import { useMutationCreateCollaboratorDeduction } from "../../../../query/payments/mutations";

type Props = {
  onClose: () => void;
};

export const AddPayeeDeduction: React.FC<Props> = ({ onClose }) => {
  const clientId = useUserId();

  const [createDeduction, createDeductionMeta] =
    useMutationCreateCollaboratorDeduction();

  return (
    <WSForm<{
      name: string;
      amount: number;
      startDate: Date;
      payeeId: string;
      detail: string;
    }>
      defaultValues={{
        payeeId: "",
        startDate: new Date(),
        detail: "",
        name: "",
        amount: null as any
      }}
      validationSchema={Yup.object().shape({
        payeeId: Yup.string().required("Contractor is required"),
        name: Yup.string().required("Name is required"),
        amount: Yup.number().required("Amount is required"),
        detail: Yup.string().required("Details are required"),
        startDate: Yup.date().required("Start date is required")
      })}
      onSubmit={async formData => {
        await createDeduction(
          {
            name: formData.name,
            type: DeductionType.PrePayment,
            amount: formData.amount,
            startDate: formData.startDate,
            clientId,
            currency: Currency.USD,
            memberId: formData.payeeId,
            detail: formData.detail
          },
          {
            onSuccess() {
              onClose();
            }
          }
        );
      }}
    >
      <WSList gap="XL">
        <WSForm.Field
          name="payeeId"
          label="Contractor"
          component={SelectPayee}
        />
        <WSForm.Field
          label="Deduction name"
          name="name"
          component={WSInputText}
        />
        <WSForm.Field
          label="Deduction amount"
          name="amount"
          component={WSInputNumber}
          componentProps={{
            currency: "USD",
            maxFractionDigits: 2,
            min: 0
          }}
        />
        <WSForm.Field
          label="Start date"
          name="startDate"
          component={WSInputDate}
        />

        <WSForm.Field
          label="Deduction details"
          name="detail"
          component={WSInputTextarea}
        />

        <WSErrorMessage error={createDeductionMeta.error} contextKey="Other" />

        <WSText color="gray500">
          Scheduled deductions are completed when you pay a contractor. If a
          deduction is larger than a payable (or your specified percentage of
          the payable) the remaining balance will be deducted from the next
          payable.
        </WSText>

        <WSForm.SubmitButton fullWidth data-testid="submit">
          Add new deduction
        </WSForm.SubmitButton>
      </WSList>
    </WSForm>
  );
};

export const useAddPayeeDeduction = () =>
  useWSModal(AddPayeeDeduction, {
    title: "Add new deduction",
    size: "S"
  });
